import React, { useState, useRef, useEffect, useCallback } from "react";

const PRESETS = [
  { label: "10 сек", seconds: 10 },
  { label: "30 сек", seconds: 30 },
  { label: "1 хв", seconds: 60 },
];

function formatTime(seconds) {
  const total = Math.max(0, Math.ceil(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// Вібрація по завершенню таймера — корисно, якщо телефон на беззвучному
// режимі й звуковий сигнал не допоможе. Якщо Vibration API недоступний
// (напр. iOS Safari), просто мовчки нічого не робимо.
function vibrateFinish() {
  try {
    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200, 100, 200]);
    }
  } catch {
    // не критично
  }
}

// Звуковий сигнал через Web Audio API — кілька коротких гудків поспіль,
// щоб сигнал було чутно навіть якщо на нього не дивляться в момент завершення.
// Якщо браузер не підтримує AudioContext, просто мовчки нічого не робимо.
function playBeep() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();

    const beepCount = 4;
    const beepDuration = 0.35;
    const gap = 0.25;

    for (let i = 0; i < beepCount; i++) {
      const startAt = ctx.currentTime + i * (beepDuration + gap);
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(0.35, startAt + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + beepDuration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(startAt);
      osc.stop(startAt + beepDuration);
    }

    const totalTime = beepCount * (beepDuration + gap);
    setTimeout(() => ctx.close(), (totalTime + 0.2) * 1000);
  } catch {
    // Звук не критичний для роботи таймера — ігноруємо помилки.
  }
}

export default function RoundTimer() {
  const [duration, setDuration] = useState(null); // обраний пресет у секундах
  const [remaining, setRemaining] = useState(null); // скільки лишилось (з дробовою частиною)
  const [running, setRunning] = useState(false);

  const intervalRef = useRef(null);
  const endAtRef = useRef(null);

  const clearTick = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    const left = (endAtRef.current - Date.now()) / 1000;
    if (left <= 0) {
      setRemaining(0);
      setRunning(false);
      clearTick();
      playBeep();
      vibrateFinish();
    } else {
      setRemaining(left);
    }
  }, [clearTick]);

  function startFromSeconds(seconds) {
    clearTick();
    setDuration(seconds);
    setRemaining(seconds);
    setRunning(true);
    endAtRef.current = Date.now() + seconds * 1000;
    intervalRef.current = setInterval(tick, 200);
  }

  function togglePause() {
    if (remaining === null || remaining <= 0) return;

    if (running) {
      clearTick();
      setRunning(false);
      return;
    }

    endAtRef.current = Date.now() + remaining * 1000;
    setRunning(true);
    intervalRef.current = setInterval(tick, 200);
  }

  function resetTimer() {
    clearTick();
    setRunning(false);
    setRemaining(null);
    setDuration(null);
  }

  // Прибираємо інтервал, якщо компонент розмонтується.
  useEffect(() => clearTick, [clearTick]);

  const isFinished = duration !== null && remaining === 0;

  return (
    <div className={`round-timer ${isFinished ? "round-timer-done" : ""}`}>
      <h3>Таймер ходу</h3>

      <div className="round-timer-presets">
        {PRESETS.map((p) => (
          <button
            key={p.seconds}
            type="button"
            className={`btn btn-ghost btn-small ${
              duration === p.seconds ? "btn-toggle-active" : ""
            }`}
            onClick={() => startFromSeconds(p.seconds)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {duration !== null && (
        <div className="round-timer-display-row">
          <span className="round-timer-display">{formatTime(remaining)}</span>
          <div className="round-timer-actions">
            <button
              type="button"
              className="btn btn-ghost btn-small"
              onClick={togglePause}
              disabled={remaining <= 0}
            >
              {running ? "Пауза" : "Продовжити"}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-small"
              onClick={resetTimer}
            >
              Скинути
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
